// Auto-match an XLS column header to a system field on the location
// import row. Used by the "Map columns" step in ImportLocations to
// seed each column's dropdown with a best-guess destination so AMs
// only have to correct the misses, not author every row from scratch.
//
// Rules (in order):
//   1. Exact case-insensitive match against the system field's
//      friendly label (e.g. "State" → 'state').
//   2. Known-alias match — the alias table mirrors xlsxParse.ts's
//      COLUMN_ALIASES (so this auto-match agrees with what the
//      legacy auto-alias parser does), plus a small list of common
//      user-facing variants Scott called out in the spec.
//   3. No match: returns 'skip'.
//
// Conservative — when in doubt, defer to the AM. Wrong auto-matches
// are worse than no auto-match because the AM may save without
// noticing the bad pick.
//
// Pure: no I/O, no DOM. The component layer calls this once per XLS
// header to seed initial mapping state; the user's overrides live in
// component state thereafter.

export type SystemField =
  | 'franchiseSuburb'
  | 'state'
  | 'groupRaw'
  | 'packageLabel'
  | 'fbAdAccountUrl'
  | 'googleAdsCustomerId'
  | 'googleAdsUrl'
  | 'crmId'
  | 'gymInfo'
  | 'clientDriveUrl'
  | 'reportUrl'
  | 'metaMonthlyBudget'
  | 'googleMonthlyBudget'
  | 'offerLandingPage'
  | 'saleLandingPage'
  | 'locationLandingPage';

// User-facing labels for the dropdown. Kept terse — the column header
// on the AM's sheet is shown alongside, so the label just needs to be
// unambiguous, not self-documenting.
export const SYSTEM_FIELD_LABELS: Record<SystemField, string> = {
  franchiseSuburb: 'Suburb',
  state: 'State',
  groupRaw: 'Group',
  packageLabel: 'Package',
  fbAdAccountUrl: 'Facebook Ad Account',
  googleAdsCustomerId: 'Google Ads Customer ID',
  googleAdsUrl: 'Google Ads Account',
  crmId: 'CRM ID',
  gymInfo: 'Gym Info',
  clientDriveUrl: 'Client Drive',
  reportUrl: 'Report dashboard URL',
  metaMonthlyBudget: 'Meta Monthly Budget',
  googleMonthlyBudget: 'Google Monthly Budget',
  offerLandingPage: 'Offer Landing Page',
  saleLandingPage: 'Sale Landing Page',
  locationLandingPage: 'Location Landing Page',
};

// Stable list of every system field in display order. Used to render
// the dropdown options.
export const SYSTEM_FIELDS: SystemField[] = Object.keys(
  SYSTEM_FIELD_LABELS,
) as SystemField[];

// Aliases seeded from xlsxParse's COLUMN_ALIASES so this auto-match
// agrees with what the legacy auto-alias parser does. Plus a small
// list of user-facing variants the spec specifically called out.
// First match wins on ties (won't happen with these distinct lists).
const COLUMN_ALIASES: Record<SystemField, string[]> = {
  // The franchiseSuburb column is the de-facto "location name anchor"
  // in the current data model — server combines it with the brand's
  // clubNamePrefix to produce the location Account name. Aliases here
  // cover "Suburb" (the canonical), Snap-Master-Sheet variants, plus
  // the "Account name" / "Name" / "Location name" labels users
  // sometimes default to when they think of this as the entity's
  // identity rather than the suburb.
  franchiseSuburb: [
    'Suburb',
    'Club',
    'Club Suburb',
    'Franchise Suburb',
    'Account name',
    'Name',
    'Location name',
  ],
  state: ['State'],
  groupRaw: ['Group'],
  packageLabel: ['Package'],
  fbAdAccountUrl: ['FB Ad Account', 'Facebook Ad Account', 'Meta Ad Account'],
  // GA ID is the authoritative source of googleAdsCustomerId in the
  // XXX-XXX-XXXX format. "Google Ads Customer ID" matches the
  // verbose label exactly.
  googleAdsCustomerId: ['GA ID', 'Google Ads Customer ID', 'Google Customer ID'],
  googleAdsUrl: ['GA Ad Account', 'Google Ads Account'],
  // The client's own CRM id for the club (Snap: the GymSales company id).
  crmId: ['CRM ID', 'GymSales ID', 'Company ID', 'CRM Company ID'],
  gymInfo: ['Gym Info'],
  clientDriveUrl: ['Client Drive', 'Drive Folder'],
  reportUrl: ['Report dashboard URL', 'Report URL', 'Report Link', 'Reporting URL'],
  metaMonthlyBudget: ['Facebook Budget', 'Meta Budget', 'Meta Monthly Budget'],
  googleMonthlyBudget: ['Google Budget', 'Google Monthly Budget'],
  offerLandingPage: ['Offer Landing Page'],
  saleLandingPage: ['Sale Landing Page'],
  locationLandingPage: ['Location Landing Page'],
};

function normaliseHeader(h: string): string {
  return h.trim().toLowerCase().replace(/\s+/g, ' ');
}

export function autoMatchColumn(xlsHeader: string): SystemField | 'skip' {
  const needle = normaliseHeader(xlsHeader);
  if (needle.length === 0) return 'skip';

  // Try the friendly labels first — most AMs use these spelled out.
  for (const field of SYSTEM_FIELDS) {
    if (normaliseHeader(SYSTEM_FIELD_LABELS[field]) === needle) return field;
  }

  // Fall through to the alias list — Snap Master Sheet headers and
  // common synonyms the AM might paste in.
  for (const field of SYSTEM_FIELDS) {
    const aliases = COLUMN_ALIASES[field];
    for (const alias of aliases) {
      if (normaliseHeader(alias) === needle) return field;
    }
  }

  return 'skip';
}
